import React, { memo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { FiPlay, FiEdit, FiTrash2, FiCalendar, FiActivity, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { formatDistanceToNow } from 'date-fns';
import { SortKey, SortDirection } from './hooks/useWorkflowSortFilter';

interface Workflow {
  id: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
  phases?: any[];
  [key: string]: any;
}

interface WorkflowExecution {
  id: string;
  status: string;
  created_at: string;
  [key: string]: any;
}

interface WorkflowMetrics {
  totalExecutions?: number;
  successRate?: number;
  lastExecuted?: string;
  [key: string]: any;
}

interface WorkflowTableProps {
  workflows: Workflow[];
  executions: Record<string, WorkflowExecution[]>;
  metrics: Record<string, WorkflowMetrics>;
  onWorkflowClick?: (workflowId: string) => void;
  onWorkflowHover?: (workflowId: string) => void;
  onWorkflowDelete?: (workflowId: string, workflowName: string) => void;
  isLoading?: boolean;
  // New sort props
  sortKey?: SortKey;
  sortDir?: SortDirection;
  onSortKeyChange?: (key: SortKey) => void;
  onToggleSortDir?: () => void;
}

const WorkflowTableRow = memo(({
  workflow,
  executions,
  metrics,
  onClick,
  onHover,
  onDelete,
  darkMode
}: {
  workflow: Workflow;
  executions: WorkflowExecution[];
  metrics?: WorkflowMetrics;
  onClick?: () => void;
  onHover?: () => void;
  onDelete?: () => void;
  darkMode: boolean;
}) => {
  const lastExecuted = metrics?.lastExecuted;
  const successRate = metrics?.successRate ?? 0;
  const executionCount = metrics?.totalExecutions ?? 0;
  
  return (
    <tr 
      className={`
        border-b transition-colors duration-150 cursor-pointer
        ${darkMode 
          ? 'border-gray-700 hover:bg-gray-700/30' 
          : 'border-gray-200 hover:bg-gray-50'
        }
      `}
      onClick={onClick}
      onMouseEnter={onHover}
    >
      {/* Workflow Name & Description */}
      <td className="px-4 py-3">
        <div className="flex flex-col">
          <span className={`font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
            {workflow.name || 'Unnamed Workflow'}
          </span>
          {workflow.description && (
            <span className={`text-sm truncate max-w-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {workflow.description}
            </span>
          )}
        </div>
      </td>
      
      {/* Phases Count */}
      <td className="px-4 py-3 text-center">
        <span className={`
          inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
          ${darkMode 
            ? 'bg-blue-900 text-blue-200' 
            : 'bg-blue-100 text-blue-800'
          }
        `}>
          {workflow.phases?.length || 0} phases
        </span>
      </td>
      
      {/* Activity - Success Rate & Executions */}
      <td className="px-4 py-3">
        <div className="flex flex-col gap-1">
          {executionCount > 0 ? (
            <>
              {/* Mini progress bar */}
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden dark:bg-gray-700 max-w-[100px]">
                  <div 
                    className={`h-full rounded-full ${successRate >= 80 ? 'bg-green-500' : successRate >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                    style={{ width: `${successRate}%` }}
                  />
                </div>
                <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {successRate}%
                </span>
              </div>
              {/* Execution count */}
              <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {executionCount} execution{executionCount !== 1 ? 's' : ''}
              </span>
            </>
          ) : (
            <span className={`text-xs italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              No executions yet
            </span>
          )}
        </div>
      </td>
      
      {/* Last Executed */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <FiCalendar className={`w-3 h-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {(() => {
              const safeFormatDate = (dateValue: string | null | undefined): string => {
                if (!dateValue) return 'Never';
                try {
                  const date = new Date(dateValue);
                  if (isNaN(date.getTime())) return 'Invalid date';
                  return formatDistanceToNow(date, { addSuffix: true });
                } catch {
                  return 'Invalid date';
                }
              };
              
              return safeFormatDate(lastExecuted);
            })()}
          </span>
        </div>
      </td>
      
      {/* Created Date */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <FiCalendar className={`w-3 h-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {(() => {
              const safeFormatDate = (dateValue: string | null | undefined): string => {
                if (!dateValue) return 'Unknown';
                try {
                  const date = new Date(dateValue);
                  if (isNaN(date.getTime())) return 'Invalid date';
                  return formatDistanceToNow(date, { addSuffix: true });
                } catch {
                  return 'Invalid date';
                }
              };
              
              return safeFormatDate(workflow.created_at);
            })()}
          </span>
        </div>
      </td>
      
      {/* Updated Date */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <FiCalendar className={`w-3 h-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {(() => {
              const safeFormatDate = (dateValue: string | null | undefined): string => {
                if (!dateValue) return 'Unknown';
                try {
                  const date = new Date(dateValue);
                  if (isNaN(date.getTime())) return 'Invalid date';
                  return formatDistanceToNow(date, { addSuffix: true });
                } catch {
                  return 'Invalid date';
                }
              };
              
              return safeFormatDate(workflow.updated_at);
            })()}
          </span>
        </div>
      </td>
      
      {/* Actions */}
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity md:opacity-100">
          <button
            className={`
              p-1.5 rounded transition-colors
              ${darkMode 
                ? 'hover:bg-green-900/50 text-green-400' 
                : 'hover:bg-green-100 text-green-600'
              }
            `}
            onClick={(e) => {
              e.stopPropagation();
              // Execute action
            }}
            title="Execute workflow"
          >
            <FiPlay className="w-4 h-4" />
          </button>
          <button
            className={`
              p-1.5 rounded transition-colors
              ${darkMode 
                ? 'hover:bg-blue-900/50 text-blue-400' 
                : 'hover:bg-blue-100 text-blue-600'
              }
            `}
            onClick={(e) => {
              e.stopPropagation();
              onClick?.();
            }}
            title="Edit workflow"
          >
            <FiEdit className="w-4 h-4" />
          </button>
          <button
            className={`
              p-1.5 rounded transition-colors
              ${darkMode
                ? 'hover:bg-red-900/50 text-red-400'
                : 'hover:bg-red-100 text-red-600'
              }
            `}
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.();
            }}
            title="Delete workflow"
          >
            <FiTrash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
});

export const WorkflowTable: React.FC<WorkflowTableProps> = ({
  workflows,
  executions,
  metrics,
  onWorkflowClick,
  onWorkflowHover,
  onWorkflowDelete,
  isLoading,
  // Destructure new props
  sortKey,
  sortDir,
  onSortKeyChange,
  onToggleSortDir
}) => {
  const { darkMode } = useTheme();
  
  // Handle header click - toggle direction if same column, otherwise switch column
  const handleHeaderClick = (key: SortKey) => {
    if (sortKey === key) {
      onToggleSortDir?.();
    } else {
      onSortKeyChange?.(key);
    }
  };
  
  // Render sort indicator
  const SortIndicator = ({ columnKey }: { columnKey: SortKey }) => {
    if (sortKey !== columnKey) return null;
    return sortDir === 'asc' ? (
      <FiArrowUp className="w-3 h-3 ml-1 opacity-70" />
    ) : (
      <FiArrowDown className="w-3 h-3 ml-1 opacity-70" />
    );
  };
  
  // Header cell component with click handling
  const HeaderCell = ({ 
    children, 
    sortKey: key, 
    align = 'left',
    className = ''
  }: { 
    children: React.ReactNode; 
    sortKey?: SortKey; 
    align?: 'left' | 'center' | 'right';
    className?: string;
  }) => {
    const isClickable = !!key;
    const alignClass = align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';
    
    return (
      <th 
        className={`
          px-4 py-3 text-xs font-medium uppercase tracking-wider ${alignClass}
          ${isClickable ? 'cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors' : ''}
          ${darkMode ? 'text-gray-400' : 'text-gray-500'}
          ${className}
        `}
        onClick={() => key && handleHeaderClick(key)}
      >
        <div className={`flex items-center ${align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : 'justify-start'}`}>
          {children}
          {key && <SortIndicator columnKey={key} />}
        </div>
      </th>
    );
  };
  
  if (isLoading) {
    return (
      <div className="w-full">
        {[...Array(5)].map((_, i) => (
          <div 
            key={i} 
            className={`
              animate-pulse flex items-center gap-4 p-4 border-b
              ${darkMode ? 'border-gray-700' : 'border-gray-200'}
            `}
          >
            <div className={`h-4 rounded w-1/3 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
            <div className={`h-4 rounded w-16 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
            <div className={`h-4 rounded w-24 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
            <div className={`h-4 rounded w-20 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
          </div>
        ))}
      </div>
    );
  }
  
  if (workflows.length === 0) {
    return (
      <div className={`
        text-center py-12 rounded-lg border-2 border-dashed
        ${darkMode 
          ? 'bg-gray-800/50 border-gray-700 text-gray-400' 
          : 'bg-gray-50 border-gray-200 text-gray-500'
        }
      `}>
        <FiActivity className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p className="text-lg font-medium">No workflows found</p>
        <p className="text-sm">Create your first workflow to get started</p>
      </div>
    );
  }
  
  return (
    <div className={`
      overflow-hidden rounded-lg border shadow-sm
      ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}
    `}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className={`
            text-left text-xs font-medium uppercase tracking-wider
            ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}
          `}>
            <tr>
              <HeaderCell sortKey="name">Workflow</HeaderCell>
              <HeaderCell align="center">Phases</HeaderCell>
              <HeaderCell sortKey="successRate">Activity</HeaderCell>
              <HeaderCell sortKey="lastExecuted">Last Executed</HeaderCell>
              <HeaderCell sortKey="created">Created</HeaderCell>
              <HeaderCell sortKey="updated">Updated</HeaderCell>
              <HeaderCell align="right">Actions</HeaderCell>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {workflows.map((workflow) => (
              <WorkflowTableRow
                key={workflow.id}
                workflow={workflow}
                executions={executions[workflow.id] || []}
                metrics={metrics[workflow.id]}
                onClick={() => onWorkflowClick?.(workflow.id)}
                onHover={() => onWorkflowHover?.(workflow.id)}
                onDelete={onWorkflowDelete ? () => onWorkflowDelete(workflow.id, workflow.name || 'Unnamed Workflow') : undefined}
                darkMode={darkMode}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
